import { PromotionDealType, PromotionHotelRow, PromotionNight } from '@app/model/promotion/PromotionOverview';

const LABELS: Record<string, string> = {
	GETAWAY_DEAL: 'Getaway Deal',
	BONUS_SAVINGS: 'Bonus savings',
	LAST_MINUTE_DEAL: 'Last-minute Deal',
	EARLY_BOOKER_DEAL: 'Early Booker Deal',
	LIMITED_TIME_DEAL: 'Limited-time Deal',
	WEEKLY_RATE: 'Weekly rate',
	BOOKING_FUNDED: 'Booking.com-funded discount',
	UNKNOWN_PROMOTION: 'Other promotion',
};

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const promotionLabel = (code: string): string =>
	LABELS[code] ?? code.toLowerCase().split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

export const promotionPillLabel = (code: string): string =>
	code === 'BOOKING_FUNDED' ? 'Booking.com-funded' : promotionLabel(code);

const BOOKING_FUNDED_NAME = 'Booking.com-funded';

// The pill shows the Deal Name, as Booking names the deal. A Booking-funded discount keeps our name, because the
// switch and its help text use it. A stored deal without a name (older scans) shows our fixed label.
export const dealTypeLabel = (type: PromotionDealType): string =>
	type.bookingFunded ? BOOKING_FUNDED_NAME : (type.name ?? promotionPillLabel(type.code));

export const formatDay = (iso: string): string => {
	const [, month, day] = iso.split('-');
	return `${Number(day)} ${MONTH_SHORT[Number(month) - 1]}`;
};

export const formatDayYear = (iso: string): string => {
	const [year, month, day] = iso.split('-');
	return `${Number(day)} ${MONTH_SHORT[Number(month) - 1]} ${year.slice(2)}`;
};

export const isHotelPaid = (row: PromotionHotelRow): boolean =>
	row.main || row.types.length === 0 || row.types.some(type => !type.bookingFunded);

export const nightTintClass = (night: PromotionNight): string => {
	if (!night.tracked) return 'bg-gray-50';
	if (!night.available) return 'bg-gray-300';
	if (night.percentage == null || night.percentage === 0) return 'bg-white';
	if (night.percentage < 15) return 'bg-emerald-50';
	if (night.percentage < 35) return 'bg-emerald-100';
	if (night.percentage < 55) return 'bg-emerald-200';
	if (night.percentage < 75) return 'bg-emerald-300';
	return 'bg-emerald-400';
};

export const nightTooltip = (hotelName: string, night: PromotionNight): string => {
	if (!night.tracked) return `${hotelName} — ${formatDayYear(night.statDate)}: not tracked yet`;
	if (!night.available) return `${hotelName} — ${formatDayYear(night.statDate)}: sold out, so we cannot see a deal`;
	const minimumStay = night.minimumStay == null ? '' : ` · minimum stay ${night.minimumStay} nights`;
	if (night.percentage == null || night.percentage === 0)
		return `${hotelName} — ${formatDayYear(night.statDate)}: no hotel-paid deal${minimumStay}`;
	const name = night.dealBookingFunded === true ? BOOKING_FUNDED_NAME : night.dealName;
	const deal = name == null ? 'a deal at' : `${name},`;
	return `${hotelName} — ${formatDayYear(night.statDate)}: ${deal} about ${Math.round(night.percentage)}% off${minimumStay}`;
};
